'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PillText } from '@web/atoms';
import { IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import { useMediaPreferences } from '../../hooks/useMediaPreferences';
import { StyledMuxPlayer } from '../MuxVideo/styles.js';
import { getPlainText } from '../../cms/RichText/getPlainText';
import { getVideoPosterSrc } from './getVideoPosterSrc';
import { videoPlaying } from './events';
import {
  CardWrapper,
  CardInteractive,
  CardFrame,
  CardSurface,
  CardMedia,
  CardMediaPlayer,
  CardGradient,
  CardOverlayTop,
  CardHandleLink,
  CardQuoteBlock,
  CardQuote,
  CardAttribution,
  PlayButtonOverlay,
  PlayButton,
} from './styles.js';

const PlayIcon = () => (
  <svg
    width="16"
    height="18"
    viewBox="0 0 16 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M15 9L1 1V17L15 9Z" fill="#4C0F51" />
  </svg>
);

const isVideoAutoplayEnabled = video => video?.autoplay === true;

const StoryCardMedia = ({
  mediaType,
  video,
  image,
  preferStaticMedia,
  isInView,
  parentHeading = '',
}) => {
  const reporter = useReporter();
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const viewVideoReported = useRef(false);
  const isImageCard = mediaType === 'image' && Boolean(image?.src);
  const posterSrc = isImageCard ? null : getVideoPosterSrc(video);
  const shouldAutoplay =
    !isImageCard &&
    Boolean(video?.playbackId) &&
    !preferStaticMedia &&
    isVideoAutoplayEnabled(video) &&
    isInView;

  useEffect(() => {
    if (!shouldAutoplay) {
      setIsPlayerVisible(false);
    }
  }, [shouldAutoplay]);

  const reportVideoViewed = () => {
    if (!viewVideoReported.current)
      reporter.tag(videoPlaying(getPlainText(parentHeading)));
    viewVideoReported.current = true;
  };

  if (isImageCard) {
    return (
      <CardMedia>
        <ResponsiveImage {...image} sizes={IMAGE_SIZES.storyCard} />
      </CardMedia>
    );
  }

  if (!video?.playbackId) {
    return null;
  }

  const posterAlt = video?.thumbnailAlt || image?.alt || 'Video thumbnail';

  if (!posterSrc && !shouldAutoplay) {
    return null;
  }

  return (
    <CardMedia>
      {posterSrc ? (
        <ResponsiveImage
          src={posterSrc}
          alt={posterAlt}
          fill
          sizes={IMAGE_SIZES.storyCard}
          style={{ objectFit: 'cover' }}
        />
      ) : null}
      {shouldAutoplay ? (
        <CardMediaPlayer $visible={isPlayerVisible}>
          <StyledMuxPlayer
            playbackId={video.playbackId}
            aspectRatio="9 / 16"
            autoPlay
            muted
            loop={video.loop}
            poster=""
            onPlaying={() => {
              setIsPlayerVisible(true);
              reportVideoViewed();
            }}
          />
        </CardMediaPlayer>
      ) : null}
    </CardMedia>
  );
};

StoryCardMedia.propTypes = {
  mediaType: PropTypes.oneOf(['video', 'image']).isRequired,
  video: PropTypes.object,
  image: PropTypes.object,
  preferStaticMedia: PropTypes.bool.isRequired,
  isInView: PropTypes.bool.isRequired,
  parentHeading: PropTypes.node,
};

export const StoryCard = ({
  mediaType = 'video',
  video,
  image,
  quote,
  attribution,
  modalOnClick = true,
  pillText,
  pillVariant = 'dark',
  pillIcon,
  pillIconAlignment = 'left',
  handleText,
  handleTarget,
  cardIndex,
  onOpenModal,
  parentHeading = '',
}) => {
  const { preferStaticMedia } = useMediaPreferences();
  const cardSurfaceRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (
      preferStaticMedia ||
      mediaType !== 'video' ||
      !video?.playbackId ||
      video?.autoplay !== true
    ) {
      setIsInView(false);
      return undefined;
    }

    const element = cardSurfaceRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting && entry.intersectionRatio >= 0.5);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [preferStaticMedia, mediaType, video?.playbackId, video?.autoplay]);

  const hasHandleLink = Boolean(handleTarget?.target && handleText);
  const showPlayButton =
    mediaType === 'video' && modalOnClick && isVideoAutoplayEnabled(video);

  const handleOpenModalClick = useCallback(() => {
    if (cardIndex != null) {
      onOpenModal?.(cardIndex);
      return;
    }

    onOpenModal?.();
  }, [cardIndex, onOpenModal]);

  const modalAriaLabel =
    mediaType === 'video'
      ? `Play video testimonial from ${attribution}: ${quote}`
      : `View image from ${attribution}: ${quote}`;

  const cardBody = (
    <CardSurface ref={cardSurfaceRef}>
      <StoryCardMedia
        mediaType={mediaType}
        video={video}
        image={image}
        preferStaticMedia={preferStaticMedia}
        isInView={isInView}
        parentHeading={parentHeading}
      />
      <CardGradient />
      {pillText && (
        <CardOverlayTop>
          <PillText
            text={pillText}
            variant={pillVariant}
            icon={pillIcon}
            iconAlignment={pillIconAlignment}
            size="small"
          />
        </CardOverlayTop>
      )}
      {showPlayButton && (
        <PlayButtonOverlay
          aria-hidden="true"
          data-testid="story-card-play-button"
        >
          <PlayButton>
            <PlayIcon />
          </PlayButton>
        </PlayButtonOverlay>
      )}
      <CardQuoteBlock>
        <CardQuote>{quote}</CardQuote>
        <CardAttribution>{attribution}</CardAttribution>
      </CardQuoteBlock>
    </CardSurface>
  );

  return (
    <CardWrapper>
      {hasHandleLink && (
        <CardHandleLink
          href={handleTarget.target}
          $hasPill={Boolean(pillText)}
          {...(handleTarget.openNewTab
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          {handleText}
        </CardHandleLink>
      )}

      {modalOnClick ? (
        <CardInteractive
          type="button"
          aria-label={modalAriaLabel}
          onClick={handleOpenModalClick}
        >
          {cardBody}
        </CardInteractive>
      ) : (
        <CardFrame aria-label={`${quote} — ${attribution}`}>
          {cardBody}
        </CardFrame>
      )}
    </CardWrapper>
  );
};

StoryCard.displayName = 'StoryCard';

StoryCard.propTypes = {
  mediaType: PropTypes.oneOf(['video', 'image']),
  video: PropTypes.shape({
    playbackId: PropTypes.string,
    thumbnail: PropTypes.string,
    thumbnailAlt: PropTypes.string,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
  }),
  image: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  quote: PropTypes.string.isRequired,
  attribution: PropTypes.string.isRequired,
  modalOnClick: PropTypes.bool,
  pillText: PropTypes.string,
  pillVariant: PropTypes.string,
  pillIcon: PropTypes.object,
  pillIconAlignment: PropTypes.oneOf(['left', 'right']),
  handleText: PropTypes.string,
  handleTarget: PropTypes.shape({
    target: PropTypes.string,
    openNewTab: PropTypes.bool,
  }),
  cardIndex: PropTypes.number,
  onOpenModal: PropTypes.func,
  parentHeading: PropTypes.node,
};
